import {
  createElement as createLucideIcon,
  Footprints,
  Bird,
  Frame,
  Palette,
  LocateFixed,
  Route,
  Play,
  Pause,
  RotateCcw,
  X,
  Ship,
  Volume2,
  VolumeX,
  Moon,
  Dice5,
  Hand,
  BookOpen,
  Menu,
  Eye,
  UserRound,
  UsersRound,
  MessageCircle,
  Zap,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUpToLine,
  ArrowDownToLine,
  Send,
} from 'lucide';
import type { HudIcon } from './types';

export type UiIcon =
  | HudIcon
  | 'palette'
  | 'recenter'
  | 'tour'
  | 'play'
  | 'pause'
  | 'restart'
  | 'close'
  | 'boat'
  | 'sound'
  | 'muted'
  | 'moon'
  | 'dice'
  | 'gesture'
  | 'book'
  | 'menu'
  | 'first-person'
  | 'behind'
  | 'chat'
  | 'boost'
  | 'arrow-up'
  | 'arrow-down'
  | 'arrow-left'
  | 'arrow-right'
  | 'ascend'
  | 'descend'
  | 'send'
  | 'visitors';

/* The ochre of the loading bar and the HUD icons (styles.css, hud.css), for places CSS cannot
   reach such as the favicon. */
export const UI_ACCENT = '#8d8650';

const ICON_MAP = {
  walk: Footprints,
  fly: Bird,
  painter: Frame,
  palette: Palette,
  recenter: LocateFixed,
  tour: Route,
  play: Play,
  pause: Pause,
  restart: RotateCcw,
  close: X,
  boat: Ship,
  sound: Volume2,
  muted: VolumeX,
  moon: Moon,
  dice: Dice5,
  gesture: Hand,
  book: BookOpen,
  menu: Menu,
  'first-person': Eye,
  behind: UserRound,
  chat: MessageCircle,
  boost: Zap,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  ascend: ArrowUpToLine,
  descend: ArrowDownToLine,
  send: Send,
  visitors: UsersRound,
} as const;

/* lucide's rendering defaults; the geometry itself always comes from the library's icon node. */
const SVG_ROOT = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: '24',
  height: '24',
  viewBox: '0 0 24 24',
  fill: 'none',
  'stroke-width': '2',
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
} as const;

export function createIcon(name: UiIcon, size = 24): SVGElement {
  const IconComponent = ICON_MAP[name];
  const svg = createLucideIcon(IconComponent);
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  return svg;
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function attributes(attrs: Readonly<Record<string, string | number | undefined>>): string {
  return Object.entries(attrs)
    .filter(([, value]) => value !== undefined)
    .map(([name, value]) => `${name}="${escapeAttribute(String(value))}"`)
    .join(' ');
}

export function iconSvg(name: UiIcon, stroke: string): string {
  const children = ICON_MAP[name].map(([tag, attrs]) => `<${tag} ${attributes(attrs)}/>`);
  return `<svg ${attributes({ ...SVG_ROOT, stroke })}>${children.join('')}</svg>`;
}

export function iconDataUrl(name: UiIcon, stroke: string): string {
  return `data:image/svg+xml,${encodeURIComponent(iconSvg(name, stroke))}`;
}
